import React from 'react';
import { AnimalAvatar, Icon } from '../../components';
import { useIdentity } from '../../hooks/useIdentity';
import './UserMiniCard.css';

// Seeded "vibe" descriptor under the handle. The old "periwinkle · cat"
// label was tied to AnimalAvatar's defunct palette/variant taxonomy; with
// DiceBear bottts-neutral the avatars are little bots, so the descriptor
// is now `${adjective} bot` derived from the same FNV hash. Hash + adjective
// pool sized so a reroll changes the label alongside the avatar.
const hash = (s) => {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};

const ADJECTIVES = [
  'curious',
  'wandering',
  'sleepy',
  'sharp',
  'glacial',
  'static',
  'quiet',
  'loud',
  'tiny',
  'fierce',
  'cosmic',
  'mossy',
];

const descriptorFor = (seed) => {
  const h = hash(seed || 'glacier');
  return `${ADJECTIVES[h % ADJECTIVES.length]} bot`;
};

/**
 * UserMiniCard — pinned to the bottom of <DesktopSidebar />.
 * Shows the visitor's avatar + handle + palette descriptor + a reroll action.
 *
 * The avatar seed comes from useIdentity().avatarSeed (= base + salt) so a
 * reroll here propagates to MobileNav, UserModal, OnboardingScreen, and any
 * self-message bubble that overrides its stored seed.
 *
 * `onRerollClick` is kept as an optional prop so callers (tests / future
 * orchestrations) can observe the intent, but the actual seed change happens
 * via useIdentity().rerollAvatar().
 */
const UserMiniCard = ({ onUserClick, onRerollClick }) => {
  const { handle, avatarSeed, rerollAvatar } = useIdentity();
  const display = handle || 'guest';
  const descriptor = descriptorFor(avatarSeed);

  const handleCardClick = () => onUserClick && onUserClick();
  const handleReroll = (e) => {
    e.stopPropagation();
    rerollAvatar();
    if (onRerollClick) onRerollClick();
  };

  return (
    <div className="user-mini-card" role="group" aria-label="Your identity">
      <button
        type="button"
        className="user-mini-card__main"
        onClick={handleCardClick}
        aria-label={`Open profile for ${display}`}
      >
        <AnimalAvatar seed={avatarSeed} size={40} />
        <span className="user-mini-card__meta">
          <span className="user-mini-card__handle">{display}</span>
          <span className="user-mini-card__descriptor">{descriptor}</span>
        </span>
      </button>
      <button
        type="button"
        className="user-mini-card__reroll"
        aria-label="Reroll avatar"
        onClick={handleReroll}
      >
        <Icon name="refresh" size={14} />
      </button>
    </div>
  );
};

export default UserMiniCard;
